/**
 * Motion presets — Solvait-AIHR Console.
 *
 * Per DESIGN_SYSTEM.md §5: motion is communication, not decoration.
 * Every preset here answers a specific user question ("Where did
 * this go?" "Did my action take effect?" "Is the system thinking?").
 *
 * Values are also mirrored into tokens.css so Tailwind utility
 * classes (`duration-small`, `ease-out`) and framer-motion presets
 * stay in lockstep. If you change a duration here, change it in
 * tokens.css too.
 *
 * Reduced-motion: every preset is wrapped in a helper that returns
 * an instant transition when `prefers-reduced-motion: reduce` is
 * active. Use `withReducedMotion(preset)` whenever framer-motion is
 * the consumer; the CSS variables in tokens.css already zero out
 * automatically for CSS-driven transitions.
 */

import type { Transition } from "framer-motion";

// Duration tokens (ms) — must match --motion-* in tokens.css.
export const DURATION = {
  instant: 0,
  micro: 150,
  small: 250,
  medium: 400,
  large: 600,
} as const;

// framer-motion's `Easing` accepts a 4-tuple cubic-bezier control
// array. Declaring the tuple type explicitly (rather than `as const`)
// keeps values mutable in framer-motion's API while preserving shape.
type CubicBezier = [number, number, number, number];

// Easing curves — must match --ease-* in tokens.css.
export const EASE: Record<"out" | "in" | "inOut" | "spring", CubicBezier> = {
  out: [0.16, 1, 0.3, 1],
  in: [0.7, 0, 0.84, 0],
  inOut: [0.65, 0, 0.35, 1],
  spring: [0.34, 1.56, 0.64, 1],
};

/**
 * Standard transition presets, indexed by intent. Use these instead
 * of inline `{ duration, ease }` objects so the system stays
 * consistent. Adding a new preset requires updating this file +
 * surfacing the use case in DESIGN_SYSTEM.md.
 */
export const TRANSITION: Record<string, Transition> = {
  // Hover states, small toggles, focus rings.
  hover: { duration: DURATION.micro / 1000, ease: EASE.out },
  // Modal entrance: fade + scale, snappy.
  modalEnter: { duration: DURATION.small / 1000, ease: EASE.out },
  modalExit: { duration: DURATION.small / 1000, ease: EASE.in },
  // Drawer slide (right in LTR, left in RTL — handled by component).
  drawerEnter: { duration: DURATION.medium / 1000, ease: EASE.out },
  drawerExit: { duration: DURATION.medium / 1000, ease: EASE.in },
  // Page-level fade — used by route transitions.
  pageFade: { duration: DURATION.medium / 1000, ease: EASE.inOut },
  // Reserved — celebrations / onboarding only.
  celebrate: { duration: DURATION.large / 1000, ease: EASE.spring },
};

/**
 * Variant presets for framer-motion's `<motion.* variants={}>` API.
 * Cover the most common entrance / exit shapes.
 */
export const VARIANTS = {
  // Fade + slight scale-in. Modals, popovers.
  fadeScale: {
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1, transition: TRANSITION.modalEnter },
    exit: { opacity: 0, scale: 0.96, transition: TRANSITION.modalExit },
  },
  // Slide from the right (LTR) — caller flips for RTL via dir prop.
  slideInRight: {
    initial: { x: "100%", opacity: 0 },
    animate: { x: 0, opacity: 1, transition: TRANSITION.drawerEnter },
    exit: { x: "100%", opacity: 0, transition: TRANSITION.drawerExit },
  },
  slideInLeft: {
    initial: { x: "-100%", opacity: 0 },
    animate: { x: 0, opacity: 1, transition: TRANSITION.drawerEnter },
    exit: { x: "-100%", opacity: 0, transition: TRANSITION.drawerExit },
  },
  // Subtle page fade. Use sparingly — content should appear
  // immediately, not after a 400ms wait.
  pageFade: {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: TRANSITION.pageFade },
    exit: { opacity: 0, transition: TRANSITION.pageFade },
  },
} as const;

/**
 * Detect prefers-reduced-motion at call time. Use in components to
 * gate motion logic that doesn't go through CSS variables.
 *
 * SSR-safe (returns false when window is undefined).
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Wrap a Transition with an instant-replacement when reduced motion
 * is on. framer-motion respects `duration: 0` as a no-op.
 */
export function withReducedMotion(t: Transition): Transition {
  if (prefersReducedMotion()) return { duration: 0 };
  return t;
}
